// /admin/fallbacks — list of letters that fell back to the safe
// template after the verifier exhausted retries. List → detail
// pattern (Stripe Radar shape). Click a row → /admin/fallbacks/$id.

import { Link, createFileRoute } from '@tanstack/react-router';
import { type AdminFallbackSummary, listAdminFallbacks } from '../lib/api';

export const Route = createFileRoute('/admin/fallbacks/')({
  loader: async () => {
    const fallbacks = await listAdminFallbacks();
    return { fallbacks };
  },
  component: FallbacksPage,
});

function FallbacksPage() {
  const { fallbacks } = Route.useLoaderData();
  const pending = fallbacks.filter((f) => !f.adminReviewedAt);
  const reviewed = fallbacks.filter((f) => f.adminReviewedAt);

  return (
    <section>
      <header className="mb-6">
        <h2 className="font-display text-2xl text-ash-50 tracking-wide">Fallback letters</h2>
        <p className="mt-1 font-body text-ash-500 text-sm italic">
          The verifier rejected these after three retry attempts and the bond received the safe
          template.
        </p>
      </header>

      {fallbacks.length === 0 ? (
        <p className="font-body text-ash-300">No fallbacks recorded.</p>
      ) : (
        <>
          {pending.length > 0 && (
            <FallbackGroup
              label={`Pending review (${pending.length})`}
              items={pending}
              tone="pending"
            />
          )}
          {reviewed.length > 0 && (
            <FallbackGroup
              label={`Reviewed (${reviewed.length})`}
              items={reviewed}
              tone="reviewed"
            />
          )}
        </>
      )}
    </section>
  );
}

function FallbackGroup({
  label,
  items,
  tone,
}: {
  label: string;
  items: AdminFallbackSummary[];
  tone: 'pending' | 'reviewed';
}) {
  return (
    <div className="mb-8 last:mb-0">
      <p className="mb-3 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
        {label}
      </p>
      <ul className="space-y-2">
        {items.map((f) => (
          <li key={f.id}>
            <Link
              to="/admin/fallbacks/$id"
              params={{ id: f.id }}
              className={`block rounded-sm border p-4 transition ${
                tone === 'pending'
                  ? 'border-accent/30 bg-paper-900/40 hover:border-accent/60 hover:bg-paper-800/50'
                  : 'border-paper-800/50 bg-paper-900/20 hover:border-paper-700 hover:bg-paper-900/40'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.32em]">
                  {f.letterType}
                </span>
                <span className="font-display text-ash-300 text-sm">{f.emberName}</span>
                <span className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
                  {f.primaryElement}
                </span>
                {f.retryCount > 0 && (
                  <span className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
                    retries: {f.retryCount}
                  </span>
                )}
                <span className="ml-auto font-display text-[0.6rem] text-ash-400">
                  {new Date(f.generatedAt).toISOString().slice(0, 16).replace('T', ' ')}
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
